/**
 * Laudo (report) viewer — design brief screen 4, the notification deep-link
 * target: `/ordens-servico/:osId/laudo/:itemId/:tipo` (legacy form) redirects
 * here with the locale prefixed.
 *
 * The photos are the whole point — evidence of care, not an inspection report.
 * Photo `value`s arrive as short-lived signed URLs (the API signs them
 * per-request, ~1 h): render them immediately, never persist them, and treat a
 * broken image after a long-lived tab as "refetch", not as a bug.
 *
 * The brief's before/after comparison needs BOTH laudos of the item on one
 * screen and is a deliberate follow-up; this page renders ONE laudo, which is
 * what a notification names.
 *
 * Same four outcomes as `OrdemServicoDetail`: loading → status line; 404 (or a
 * `tipo` that is not on the wire enum) → "not found" EmptyState; other failure
 * → honest error with retry; the laudo.
 */
import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';

import { EmptyState } from '../components/EmptyState.tsx';
import { IconAlert, IconSearch } from '../components/icons.tsx';
import { useAuth } from '../auth/AuthContext.tsx';
import { getReport, type ReportAttribute, type ReportDetail, type ReportItemAttribute } from '../api/serviceOrders.ts';
import { formatDateTime } from '../i18n/format.ts';
import { currentLocale } from '../i18n/index.ts';
import { buildPath } from '../i18n/routes.ts';
import { runtimeEnv } from '../runtimeConfig.ts';
import styles from './LaudoPage.module.css';

function isReportType(value: string | undefined): value is 'entry' | 'exit' {
  return value === 'entry' || value === 'exit';
}

export function LaudoPage(): React.JSX.Element {
  const { osId, itemId, tipo } = useParams();
  const { t } = useTranslation();
  const auth = useAuth();
  const accessToken = auth.session?.access_token;
  const bffBase = runtimeEnv('VITE_BFF_BASE_URL') ?? '/bff/v1';
  const validType = isReportType(tipo);

  const [openPhoto, setOpenPhoto] = useState<ReportAttribute | null>(null);

  const closePhoto = useCallback(() => setOpenPhoto(null), []);
  useEffect(() => {
    if (!openPhoto) return undefined;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') closePhoto();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [openPhoto, closePhoto]);

  const { data, isLoading, isError, error, refetch } = useQuery<ReportDetail, Error>({
    queryKey: ['reports', itemId, tipo],
    queryFn: async () => {
      const result = await getReport(fetch, bffBase, accessToken ?? '', itemId ?? '', tipo as 'entry' | 'exit');
      if (result.error) throw new Error(result.error);
      if (!result.data) throw new Error('server');
      return result.data;
    },
    enabled: Boolean(accessToken && itemId && validType),
  });

  // A `tipo` outside the wire enum can only come from a typed-in URL — the same
  // customer-facing dead end as a report that does not exist.
  if (!validType || (isError && error.message === 'not_found')) {
    return (
      <EmptyState
        icon={<IconSearch size={32} />}
        title={t('laudo.notFoundTitle')}
        description={t('laudo.notFoundBody')}
      />
    );
  }

  if (isLoading || !data) {
    if (isError) {
      return (
        <div className="ns-alert" role="alert">
          <IconAlert size={18} />
          <div>
            <p className={styles['errorTitle']}>{t('laudo.loadErrorTitle')}</p>
            <p className={styles['errorBody']}>{t('laudo.loadErrorBody')}</p>
            <button type="button" className={`ns-cta ${styles['errorRetry']}`} onClick={() => void refetch()}>
              {t('orderDetail.retry')}
            </button>
          </div>
        </div>
      );
    }

    return <p className={styles['status']}>{t('laudo.loading')}</p>;
  }

  const photos = data.attributes.filter((a) => a.type === 'photo');
  const details = data.attributes.filter((a) => a.type !== 'photo');
  const itemAttrs: ReportItemAttribute[] = data.item_attributes ?? [];
  const backTo = buildPath('ordemServicoDetalhe', currentLocale(), {
    osId: osId ?? String(data.order_id),
  });

  return (
    <div className={styles['page']}>
      <header className={styles['header']}>
        <Link to={backTo} className={styles['back']}>
          {t('laudo.backToOrder', { numero: data.order_id })}
        </Link>

        <h1 className={styles['title']}>{t(data.type === 'entry' ? 'report.entry' : 'report.exit')}</h1>

        {/* O mesmo contexto que o laudo público compartilhável mostra: loja,
            pedido, item e etiqueta — o cliente com dois pedidos precisa saber
            de QUAL item este laudo fala sem sair da página. */}
        <dl className={styles['meta']}>
          <div className={styles['metaRow']}>
            <dt className={styles['metaName']}>{t('laudo.store')}</dt>
            <dd className={styles['metaValue']}>{data.franchise.name}</dd>
          </div>
          <div className={styles['metaRow']}>
            <dt className={styles['metaName']}>{t('laudo.order')}</dt>
            <dd className={styles['metaValue']}>{data.order_id}</dd>
          </div>
          <div className={styles['metaRow']}>
            <dt className={styles['metaName']}>{t('laudo.item')}</dt>
            <dd className={styles['metaValue']}>{data.item_description}</dd>
          </div>
          {data.item_qrcode && (
            <div className={styles['metaRow']}>
              <dt className={styles['metaName']}>{t('laudo.qrcode')}</dt>
              <dd className={styles['metaValue']}>{data.item_qrcode}</dd>
            </div>
          )}
          {data.final_status && (
            <div className={styles['metaRow']}>
              <dt className={styles['metaName']}>{t('laudo.finalStatus')}</dt>
              <dd className={styles['metaValue']}>{data.final_status}</dd>
            </div>
          )}
        </dl>

        {data.status === 'confirmed' && data.confirmed_at ? (
          <p className={styles['confirmed']}>{t('laudo.confirmedAt', { date: formatDateTime(data.confirmed_at) })}</p>
        ) : (
          <p className={styles['pending']}>{t('laudo.pendingBody')}</p>
        )}
      </header>

      {itemAttrs.length > 0 && (
        <section aria-label={t('laudo.itemAttrsTitle')}>
          <h2 className={styles['sectionTitle']}>{t('laudo.itemAttrsTitle')}</h2>
          <dl className={styles['details']}>
            {itemAttrs.map((attr) => (
              <div key={attr.attribute_id} className={styles['detailRow']}>
                <dt className={styles['detailName']}>{attr.name}</dt>
                <dd className={styles['detailValue']}>
                  {attr.type === 'boolean' ? t(attr.value === 'yes' ? 'laudo.yes' : 'laudo.no') : attr.value}
                </dd>
              </div>
            ))}
          </dl>
        </section>
      )}

      {photos.length > 0 && (
        <section aria-label={t('laudo.photosTitle')}>
          <h2 className={styles['sectionTitle']}>{t('laudo.photosTitle')}</h2>
          <ul className={styles['gallery']}>
            {photos.map((photo) => (
              <li key={photo.attribute_id}>
                <figure className={styles['photoFigure']}>
                  <button
                    type="button"
                    className={styles['photoButton']}
                    onClick={() => setOpenPhoto(photo)}
                    aria-label={t('laudo.openPhoto', { name: photo.name })}
                  >
                    <img className={styles['photo']} src={photo.value} alt={photo.name} loading="lazy" />
                  </button>
                  {/* O título da foto ("Foto de cima", "Foto de frente") é
                      informação, não decoração — visível, não só no alt. */}
                  <figcaption className={styles['photoCaption']}>{photo.name}</figcaption>
                </figure>
              </li>
            ))}
          </ul>
        </section>
      )}

      {details.length > 0 && (
        <section aria-label={t('laudo.detailsTitle')}>
          <h2 className={styles['sectionTitle']}>{t('laudo.detailsTitle')}</h2>
          <dl className={styles['details']}>
            {details.map((attr) => (
              <div key={attr.attribute_id} className={styles['detailRow']}>
                <dt className={styles['detailName']}>{attr.name}</dt>
                <dd className={styles['detailValue']}>
                  {attr.type === 'checkbox' ? t(attr.value === 'yes' ? 'laudo.yes' : 'laudo.no') : attr.value}
                </dd>
              </div>
            ))}
          </dl>
        </section>
      )}

      {data.observations && (
        <section aria-label={t('laudo.observations')}>
          <h2 className={styles['sectionTitle']}>{t('laudo.observations')}</h2>
          <p className={styles['observations']}>{data.observations}</p>
        </section>
      )}

      {photos.length === 0 && details.length === 0 && !data.observations && (
        <p className={styles['status']}>{t('laudo.emptyBody')}</p>
      )}

      {openPhoto && (
        <div
          className={styles['lightbox']}
          role="dialog"
          aria-modal="true"
          aria-label={openPhoto.name}
          onClick={closePhoto}
        >
          <img className={styles['lightboxImg']} src={openPhoto.value} alt={openPhoto.name} />
          <p className={styles['lightboxCaption']}>{openPhoto.name}</p>
          <button type="button" className={styles['lightboxClose']} onClick={closePhoto}>
            {t('laudo.closePhoto')}
          </button>
        </div>
      )}
    </div>
  );
}
